import { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ChartBar, DownloadSimple, MagnifyingGlass, Student, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import MedcofEngagementPanel from "../components/MedcofEngagementPanel";

const initialFilters = {
  scope: "all",
  metric: "percentage",
  minimum: 0,
  maximum: 100,
  semesterIds: [],
  examIds: [],
};

const number = (value, digits = 1) => (
  value === null || value === undefined ? "—" : Number(value).toFixed(digits)
);

const toggle = (values, identifier) => (
  values.includes(identifier) ? values.filter((item) => item !== identifier) : [...values, identifier]
);

function SummaryCard({ label, value, hint, icon: Icon = ChartBar }) {
  return (
    <div className="border border-border bg-card p-5 min-h-[132px] flex flex-col justify-between">
      <div className="flex justify-between gap-3"><span className="label-eyebrow">{label}</span><Icon size={19} className="text-primary" weight="duotone" /></div>
      <div><div className="font-display text-4xl font-black">{value}</div>{hint && <div className="text-xs text-muted-foreground mt-2">{hint}</div>}</div>
    </div>
  );
}

function LowExamParticipationPanel({ options }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({ classIds: [], semesterIds: [], maximumExamCount: null });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const classes = options.classes || [];
  const semesters = options.semesters || [];
  const maximumAvailable = Number(result?.available_maximum_exam_count || 0);
  const selectedLimit = filters.maximumExamCount == null
    ? maximumAvailable
    : Math.min(Number(filters.maximumExamCount || 0), maximumAvailable);

  const changeFilters = (updater) => {
    setFilters((current) => (typeof updater === "function" ? updater(current) : updater));
    setResult(null);
  };

  const runSearch = async (source = filters) => {
    setLoading(true);
    try {
      const response = await api.post("/analytics/performance/low-exam-participation", {
        class_ids: source.classIds,
        semester_ids: source.semesterIds,
        maximum_exam_count: source.maximumExamCount,
      });
      setResult(response.data);
    } catch (error) {
      toast.error("Falha ao buscar participação: " + fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  const applyLimit = (value) => {
    const next = {
      ...filters,
      maximumExamCount: value === "all" ? null : Number(value),
    };
    setFilters(next);
    runSearch(next);
  };

  return (
    <section className="border border-border bg-card p-6 mb-6" data-testid="low-exam-participation">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">acompanhamento acadêmico</div>
          <h2 className="font-display text-2xl font-black mt-1">Alunos com menos provas realizadas</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">Monte um recorte por turma e semestre para localizar rapidamente quem ainda fez poucas — ou nenhuma — avaliação.</p>
        </div>
        <Button type="button" variant={open ? "outline" : "default"} className="rounded-none" onClick={() => setOpen((value) => !value)} data-testid="low-exam-participation-toggle">
          <MagnifyingGlass size={16} weight="bold" className="mr-2" />{open ? "Fechar busca" : "Buscar alunos"}
        </Button>
      </div>

      {open && (
        <div className="mt-6 pt-6 border-t border-border space-y-5">
          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="label-eyebrow">turmas</span>
                <span className="flex gap-3 text-xs"><button type="button" className="underline" onClick={() => changeFilters((value) => ({ ...value, classIds: classes.map((item) => item.id) }))}>Todas</button><button type="button" className="underline" onClick={() => changeFilters((value) => ({ ...value, classIds: [] }))}>Limpar</button></span>
              </div>
              <div className="border border-border p-3 max-h-52 overflow-y-auto grid sm:grid-cols-2 gap-2" data-testid="low-exam-class-options">
                {classes.map((item) => <label key={item.id} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters.classIds.includes(item.id)} onChange={() => changeFilters((value) => ({ ...value, classIds: toggle(value.classIds, item.id) }))} />{item.name}</label>)}
                {!classes.length && <div className="text-sm text-muted-foreground">Nenhuma turma disponível para a sua permissão.</div>}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{filters.classIds.length || classes.length} turma(s) no recorte {filters.classIds.length ? "selecionada(s)" : "(todas)"}.</div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="label-eyebrow">semestres</span>
                <span className="flex gap-3 text-xs"><button type="button" className="underline" onClick={() => changeFilters((value) => ({ ...value, semesterIds: semesters.map((item) => item.id) }))}>Todos</button><button type="button" className="underline" onClick={() => changeFilters((value) => ({ ...value, semesterIds: [] }))}>Limpar</button></span>
              </div>
              <div className="border border-border p-3 max-h-52 overflow-y-auto grid sm:grid-cols-2 gap-2" data-testid="low-exam-semester-options">
                {semesters.map((item) => <label key={item.id} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters.semesterIds.includes(item.id)} onChange={() => changeFilters((value) => ({ ...value, semesterIds: toggle(value.semesterIds, item.id) }))} />{item.name}</label>)}
                {!semesters.length && <div className="text-sm text-muted-foreground">Nenhum semestre com prova disponível.</div>}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{filters.semesterIds.length || semesters.length} semestre(s) no recorte {filters.semesterIds.length ? "selecionado(s)" : "(todos)"}.</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-4 border border-primary/40 bg-primary/5 p-4">
            <div className="flex-1">
              <div className="label-eyebrow mb-1">limite de provas realizadas</div>
              {result ? <select value={filters.maximumExamCount == null ? "all" : String(selectedLimit)} onChange={(event) => applyLimit(event.target.value)} disabled={loading} className="w-full md:max-w-sm border border-border bg-background px-3 py-2 font-mono-data" data-testid="low-exam-count-limit">
                <option value="all">Todas as contagens (atÃ© {maximumAvailable} prova(s))</option>
                {Array.from({ length: maximumAvailable + 1 }, (_, value) => <option key={value} value={value}>AtÃ© {value} prova(s)</option>)}
              </select> : <div className="text-sm text-muted-foreground">Calcule o recorte para descobrir o maior número de provas e liberar este seletor.</div>}
              {result && <div className="mt-2 text-xs text-muted-foreground">Maior contagem encontrada no recorte: <b>{maximumAvailable}</b> prova(s). O filtro inclui alunos com <b>até</b> o limite escolhido.</div>}
            </div>
            <Button type="button" className="rounded-none min-w-56" disabled={loading || !classes.length} onClick={() => runSearch()} data-testid="low-exam-participation-run">
              <MagnifyingGlass size={16} weight="bold" className="mr-2" />{loading ? "Calculando…" : result ? "Atualizar recorte" : "Calcular provas"}
            </Button>
          </div>

          {result && <div className="border border-border">
            <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div><div className="label-eyebrow">resultado</div><div className="font-display text-xl font-black mt-1">{result.summary?.students_found || 0} aluno(s) com até {result.summary?.limit || 0} prova(s)</div></div>
              <div className="text-xs text-muted-foreground font-mono-data">{result.summary?.enrolled_students || 0} aluno(s) matriculado(s) no recorte</div>
            </div>
            <div className="divide-y divide-border max-h-[520px] overflow-y-auto" data-testid="low-exam-participation-results">
              {(result.students || []).map((student) => <a key={student.student_id} href={`/students/${student.student_id}`} className="block px-4 py-3 hover:bg-muted/30">
                <div className="grid md:grid-cols-[1fr_auto] gap-2 items-center"><div><div className="font-semibold">{student.student_name}</div><div className="text-xs text-muted-foreground">{student.class_names?.join(" · ") || "Sem turma informada"}{student.cpf_pending ? " · CPF pendente" : ""}</div></div><div className={`font-mono-data font-black text-lg ${student.exam_count === 0 ? "text-destructive" : "text-primary"}`}>{student.exam_count} prova(s)</div></div>
              </a>)}
              {!result.students?.length && <div className="p-10 text-center text-sm text-muted-foreground">Nenhum aluno ficou dentro desse limite.</div>}
            </div>
          </div>}
        </div>
      )}
    </section>
  );
}

export default function PerformanceSearchPage() {
  const [options, setOptions] = useState({ semesters: [], exams: [], classes: [] });
  const [filters, setFilters] = useState(initialFilters);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const payload = (source = filters) => ({
    metric: source.metric,
    minimum: Number(source.minimum),
    maximum: Number(source.maximum),
    semester_ids: source.semesterIds,
    exam_ids: source.scope === "exams" ? source.examIds : [],
  });

  const runSearch = async (source = filters) => {
    setLoading(true);
    try {
      const response = await api.post("/analytics/performance/search", payload(source));
      setResult(response.data);
    } catch (error) {
      toast.error("Falha na pesquisa: " + fmtError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [optionsResponse, resultResponse] = await Promise.all([
          api.get("/analytics/performance/options"),
          api.post("/analytics/performance/search", payload(initialFilters)),
        ]);
        setOptions(optionsResponse.data || { semesters: [], exams: [], classes: [] });
        setResult(resultResponse.data);
      } catch (error) {
        toast.error("Falha ao abrir a pesquisa de desempenho: " + fmtError(error));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleExams = useMemo(() => (
    !filters.semesterIds.length
      ? options.exams
      : options.exams.filter((exam) => filters.semesterIds.includes(exam.semester_id))
  ), [filters.semesterIds, options.exams]);

  const exportResults = async () => {
    setExporting(true);
    try {
      const response = await api.post("/analytics/performance/export.xlsx", payload(), { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "pesquisa_desempenho.xlsx";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Falha ao exportar: " + fmtError(error));
    } finally {
      setExporting(false);
    }
  };

  const summary = result?.summary || {};
  const resultMetric = result?.filters?.metric || filters.metric;
  const metricLabel = resultMetric === "tri" ? "nota TRI" : "% de acertos";

  return (
    <main className="max-w-[1400px] mx-auto px-8 py-10" data-testid="performance-search-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-8">
        <div>
          <div className="label-eyebrow">V41.7 · pesquisa acadêmica</div>
          <h1 className="font-display text-5xl font-black tracking-tighter mt-2">Desempenho dos alunos</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-3xl">
            Combine semestres, provas e uma faixa de notas. Cada aluno é contado uma vez pela média das avaliações selecionadas.
          </p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={exportResults} disabled={exporting || !result?.students?.length}>
          <DownloadSimple size={16} weight="bold" className="mr-2" />{exporting ? "Exportando…" : "Exportar XLSX"}
        </Button>
      </div>

      <MedcofEngagementPanel />

      <LowExamParticipationPanel options={options} />

      <section className="border border-border bg-card p-6 mb-6" data-testid="performance-filters">
        <div className="grid lg:grid-cols-2 gap-7">
          <div className="space-y-6">
            <div>
              <div className="label-eyebrow mb-2">abrangência</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setFilters((value) => ({ ...value, scope: "all" }))}
                  className={`border px-4 py-2 text-xs font-mono-data uppercase tracking-wider ${filters.scope === "all" ? "bg-foreground text-background border-foreground" : "border-border"}`}>Geral</button>
                <button type="button" onClick={() => setFilters((value) => ({ ...value, scope: "exams" }))}
                  className={`border px-4 py-2 text-xs font-mono-data uppercase tracking-wider ${filters.scope === "exams" ? "bg-foreground text-background border-foreground" : "border-border"}`}>Por prova</button>
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2">tipo de nota</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setFilters((value) => ({ ...value, metric: "percentage" }))}
                  className={`border px-4 py-2 text-xs font-mono-data uppercase tracking-wider ${filters.metric === "percentage" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>% de acertos</button>
                <button type="button" onClick={() => setFilters((value) => ({ ...value, metric: "tri" }))}
                  className={`border px-4 py-2 text-xs font-mono-data uppercase tracking-wider ${filters.metric === "tri" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Nota TRI</button>
              </div>
              {filters.metric === "tri" && <p className="text-xs text-muted-foreground mt-2">Provas sem cálculo TRI disponível não entram no resultado.</p>}
            </div>
            <div>
              <div className="flex justify-between gap-4 mb-2"><span className="label-eyebrow">faixa de nota</span><span className="font-mono-data font-bold">{filters.minimum}% — {filters.maximum}%</span></div>
              <div className="grid grid-cols-[1fr_90px] gap-3 items-center mb-3">
                <input aria-label="Nota mínima" type="range" min="0" max="100" step="1" value={filters.minimum}
                  onChange={(event) => setFilters((value) => ({ ...value, minimum: Math.min(Number(event.target.value), value.maximum) }))} className="w-full accent-primary" />
                <input aria-label="Valor mínimo" type="number" min="0" max={filters.maximum} value={filters.minimum}
                  onChange={(event) => setFilters((value) => ({ ...value, minimum: Math.max(0, Math.min(Number(event.target.value), value.maximum)) }))} className="border border-border bg-background px-3 py-2" />
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-3 items-center">
                <input aria-label="Nota máxima" type="range" min="0" max="100" step="1" value={filters.maximum}
                  onChange={(event) => setFilters((value) => ({ ...value, maximum: Math.max(Number(event.target.value), value.minimum) }))} className="w-full accent-primary" />
                <input aria-label="Valor máximo" type="number" min={filters.minimum} max="100" value={filters.maximum}
                  onChange={(event) => setFilters((value) => ({ ...value, maximum: Math.min(100, Math.max(Number(event.target.value), value.minimum)) }))} className="border border-border bg-background px-3 py-2" />
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between gap-3 mb-2"><span className="label-eyebrow">semestres</span>{filters.semesterIds.length > 0 && <button type="button" className="text-xs underline" onClick={() => setFilters((value) => ({ ...value, semesterIds: [] }))}>Todos</button>}</div>
              <div className="border border-border max-h-40 overflow-y-auto p-3 grid sm:grid-cols-2 gap-2">
                {(options.semesters || []).map((semester) => (
                  <label key={semester.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={filters.semesterIds.includes(semester.id)} onChange={() => setFilters((value) => ({ ...value, semesterIds: toggle(value.semesterIds, semester.id) }))} />
                    <span>{semester.name}</span>
                  </label>
                ))}
                {!options.semesters?.length && <span className="text-sm text-muted-foreground">Nenhum semestre com avaliações.</span>}
              </div>
            </div>
            {filters.scope === "exams" && (
              <div>
                <div className="flex items-center justify-between gap-3 mb-2"><span className="label-eyebrow">provas</span>{filters.examIds.length > 0 && <button type="button" className="text-xs underline" onClick={() => setFilters((value) => ({ ...value, examIds: [] }))}>Limpar</button>}</div>
                <div className="border border-border max-h-56 overflow-y-auto p-3 space-y-2">
                  {visibleExams.map((exam) => (
                    <label key={exam.id} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="mt-1" checked={filters.examIds.includes(exam.id)} onChange={() => setFilters((value) => ({ ...value, examIds: toggle(value.examIds, exam.id) }))} />
                      <span><span className="font-semibold">{exam.name}</span><span className="block text-xs text-muted-foreground">{exam.semester_name}</span></span>
                    </label>
                  ))}
                  {!visibleExams.length && <span className="text-sm text-muted-foreground">Nenhuma prova nesse recorte.</span>}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end mt-6 pt-5 border-t border-border">
          <Button className="rounded-none min-w-52" onClick={() => runSearch()} disabled={loading || (filters.scope === "exams" && !filters.examIds.length)}>
            <MagnifyingGlass size={16} weight="bold" className="mr-2" />{loading ? "Pesquisando…" : "Pesquisar alunos"}
          </Button>
        </div>
      </section>

      {loading && !result ? <div className="border border-border p-20 text-center label-eyebrow">calculando desempenho…</div> : result && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <SummaryCard icon={Student} label="alunos encontrados" value={summary.students || 0} hint={`${summary.attempts || 0} avaliação(ões)`} />
            <SummaryCard label={`média · ${metricLabel}`} value={number(summary.average)} />
            <SummaryCard label={`mediana · ${metricLabel}`} value={number(summary.median)} />
            <SummaryCard label="intervalo encontrado" value={`${number(summary.minimum)}–${number(summary.maximum)}`} />
          </div>

          {resultMetric === "tri" && summary.students_without_selected_metric > 0 && (
            <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-4 mb-6 flex gap-3 text-sm"><Warning size={20} weight="fill" className="text-[hsl(var(--warning))] shrink-0" />{summary.students_without_selected_metric} aluno(s) não entraram porque suas provas ainda não possuem nota TRI calculável.</div>
          )}

          <section className="border border-border bg-card p-5 mb-6">
            <div className="label-eyebrow">distribuição do resultado</div>
            <h2 className="font-display text-2xl font-black mt-1">Quantidade de alunos por faixa de {metricLabel}</h2>
            <div className="h-[340px] mt-5">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={result.distribution || []} margin={{ top: 10, right: 15, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, name, item) => [`${value} aluno(s) · ${number(item?.payload?.percentage)}%`, "Quantidade"]} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="space-y-3" data-testid="performance-student-list">
            <div className="flex items-end justify-between gap-4 mb-4"><div><div className="label-eyebrow">alunos e histórico</div><h2 className="font-display text-3xl font-black mt-1">Notas por prova e semestre</h2></div><div className="text-sm text-muted-foreground">{result.students?.length || 0} aluno(s)</div></div>
            {(result.students || []).map((studentItem) => (
              <details key={studentItem.student_id} className="border border-border bg-card group">
                <summary className="list-none cursor-pointer p-4 grid md:grid-cols-[1fr_150px_150px_150px_120px] gap-3 items-center">
                  <div><div className="font-semibold">{studentItem.student_name}</div><div className="text-xs font-mono-data text-muted-foreground">CPF {studentItem.cpf || "—"}</div></div>
                  <div><div className="label-eyebrow">média selecionada</div><div className="font-mono-data font-black text-primary">{number(studentItem.selected_score)}</div></div>
                  <div><div className="label-eyebrow">% acertos</div><div className="font-mono-data font-bold">{number(studentItem.average_percentage)}</div></div>
                  <div><div className="label-eyebrow">nota TRI</div><div className="font-mono-data font-bold">{number(studentItem.average_tri)}</div></div>
                  <div className="text-xs font-mono-data md:text-right">{studentItem.attempt_count} prova(s) ▾</div>
                </summary>
                <div className="border-t border-border overflow-x-auto">
                  <div className="min-w-[820px] grid grid-cols-[160px_1fr_130px_130px_120px] gap-3 px-4 py-3 bg-muted/30 label-eyebrow"><div>Semestre</div><div>Prova</div><div>% acertos</div><div>Nota TRI</div><div>Data</div></div>
                  {studentItem.attempts.map((attempt) => (
                    <div key={attempt.correction_id} className="min-w-[820px] grid grid-cols-[160px_1fr_130px_130px_120px] gap-3 px-4 py-3 border-t border-border text-sm items-center">
                      <div>{attempt.semester_name}</div><div className="font-semibold">{attempt.exam_name}</div><div className="font-mono-data">{number(attempt.score_pct)}%</div><div className="font-mono-data text-primary">{number(attempt.tri_score)}</div><div className="text-xs">{attempt.created_at ? new Date(attempt.created_at).toLocaleDateString("pt-BR") : "—"}</div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
            {!result.students?.length && <div className="border border-border p-16 text-center"><Student size={30} className="mx-auto text-muted-foreground mb-3" /><div className="label-eyebrow">nenhum aluno encontrado nessa faixa</div></div>}
          </section>
        </>
      )}
    </main>
  );
}
